import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Task } from 'graphql-common';
import { FieldItem, Option, Values } from '@lib/interfaces/form';
import { ErrorLike } from '@apollo/client';
import PageBody from '@lib/layouts/DashboardLayout/PageBody/PageBody';
import Form from '@lib/components/ReactHookForm/FormContainer';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import CircleLoader from '@lib/components/CircleLoader/CircleLoader';
import PageHeader from '@lib/layouts/DashboardLayout/PageHeader/PageHeader';
import ErrorPage from '@lib/components/ErrorPage/ErrorPage';
import getStatusFromError from '@lib/utils/getStatusFromError';
import getDescriptionFromError from '@lib/utils/getDescriptionFromError';
import getPrefilledQuestions from 'components/FormTemplateBuilder/utils/getPrefilledQuestions';
import getTaskValuesFormFromQueryData from 'components/Task/utils/getTaskValuesFormFromQueryData';
import { FormRefType } from '@lib/components/ReactHookForm/types';
import { getGraphQLErrors } from '@lib/utils/graphQLErrors';
import TaskEditFormActions from './TaskEditFormActions';

interface Props {
  updateMutationError?: ErrorLike;
  taskError?: ErrorLike;
  taskLoading?: boolean;
  updateTaskLoading: boolean;
  taskData: Task;
  formFields: FieldItem[];
  formRef: FormRefType;
  initialDepartmentLinks: Option[];
  initialDocumentationLinks: Option[];
  onSubmit: (values: Values) => void;
}

function TaskEdit(props: Props) {
  const {
    updateMutationError,
    updateTaskLoading,
    formFields,
    formRef,
    initialDepartmentLinks,
    initialDocumentationLinks,
    onSubmit,
    taskData,
    taskError,
    taskLoading,
  } = props;
  const { t } = useTranslation();

  // Links load after the task (they need its groupKey), so they belong in
  // defaultValues: setting them once the form is mounted marks it dirty.
  const defaultValues = useMemo<Values>(
    () =>
      taskData
        ? getTaskValuesFormFromQueryData(taskData, false, {
            documentationLinks: initialDocumentationLinks,
            departmentLinks: initialDepartmentLinks,
          })
        : {},
    [initialDepartmentLinks, initialDocumentationLinks, taskData],
  );

  const isContentVisible = !taskLoading && !taskError;

  if (taskError) {
    return (
      <ErrorPage
        status={getStatusFromError(taskError)}
        description={getDescriptionFromError(taskError)}
      />
    );
  }

  return (
    <DashboardContentMaxWidth>
      {isContentVisible && <PageHeader title={t('edit-task-details')} />}
      <PageBody withoutHeader={!isContentVisible}>
        {taskLoading ? (
          <CircleLoader />
        ) : (
          <WhiteBox verticalPaddings="large">
            <Form
              actionsComponent={TaskEditFormActions}
              defaultValues={defaultValues}
              fields={formFields}
              formId="task-edit"
              formRef={formRef}
              graphQLErrors={getGraphQLErrors(updateMutationError)}
              graphQLErrorsReplacePathMap={{
                name: 'taskName',
                description: 'taskDescription',
                'formTemplate.name': 'name',
                'formTemplate.description': 'description',
                'formTemplate.questions': 'questions',
              }}
              loading={updateTaskLoading}
              onSubmit={onSubmit}
              sourceFields={{
                questions: getPrefilledQuestions({ t }),
              }}
            />
          </WhiteBox>
        )}
      </PageBody>
    </DashboardContentMaxWidth>
  );
}

export default TaskEdit;
